import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import globalAxios from "axios";
import { createRoot } from "react-dom/client";
import { FiExternalLink, FiLink2 } from "react-icons/fi";
import { PageRenderer, RendererRuntimeProvider, type All8PageContent } from "@bcc/core-renderers";
import { LoadingSpinner } from "../LoadingSpinner";
import { createRuntimeImageComponent, resolveStorageUrl } from "../../Helper/storage";
import { PREVIEW_SITE_URL, PRODUCTION_SITE_URL, buildPublicUrl } from "../../Helper/siteLinks";
import {
	collectCardFragmentKeys,
	loadBlockFragments,
	loadCardFragments,
	overlayBlockFragments,
	overlayCardFragments,
	type BlockFragmentMap,
	type CardFragmentMap
} from "../PageEditor/blockFragmentPreview";

/**
 * La revue VISUELLE d'une publication : la page telle que la cible la sert aujourd'hui (à gauche)
 * et telle qu'elle sera après publication (à droite), rendues côte à côte par le PageRenderer du
 * SDK dans deux iframes au défilement lié. Les deux payloads viennent de
 * GET /api/deployment/render-pair (pages : recette du deploy et ledger ; fiches : projection du
 * gabarit en simulation et ligne content_items servie). Les widgets calculés (cours, inflation,
 * dernières actualités) reçoivent les fragments cuits de la cible, comme sur le site.
 */

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

export interface DeploymentRenderPairResponse {
	supported: boolean;
	reason?: string | null;
	kind?: "page" | "record" | string;
	url?: string | null;
	expected?: unknown;
	deployed?: unknown;
	identical?: boolean;
}

export interface RenderPairState {
	loading: boolean;
	error: string | null;
	pair: DeploymentRenderPairResponse | null;
}

/** Charge la paire rendable d'un contenu contre une cible ; enabled=false = ne rien faire. */
export const useDeploymentRenderPair = (
	contentId: string | null | undefined,
	target: "preview" | "production",
	enabled = true
): RenderPairState => {
	const [state, setState] = useState<RenderPairState>({ loading: !!contentId && enabled, error: null, pair: null });
	useEffect(() => {
		if (!contentId || !enabled) {
			setState({ loading: false, error: null, pair: null });
			return;
		}
		let cancelled = false;
		setState({ loading: true, error: null, pair: null });
		globalAxios
			.get<DeploymentRenderPairResponse>(`${apiBase}/api/deployment/render-pair`, {
				params: { contentId, target },
				withCredentials: true
			})
			.then((res) => {
				if (!cancelled) setState({ loading: false, error: null, pair: res.data ?? null });
			})
			.catch((err) => {
				if (cancelled) return;
				const message = err?.response?.data?.message ?? "Le rendu comparé n'a pas pu être chargé.";
				setState({ loading: false, error: String(message), pair: null });
			});
		return () => {
			cancelled = true;
		};
	}, [contentId, target, enabled]);
	return state;
};

/** Composants rendables d'un payload de page (page.json ou ligne record) ; [] quand absent. */
export const componentsOf = (payload: unknown): All8PageContent => {
	const list = (payload as { components?: unknown } | null)?.components;
	return Array.isArray(list) ? (list as All8PageContent) : [];
};

/** Largeurs de rendu proposées ; « fluide » = la largeur disponible de la colonne. */
export const PAIR_VIEWPORTS: Record<string, { label: string; width: number | null }> = {
	mobile: { label: "Mobile", width: 390 },
	tablet: { label: "Tablette", width: 768 },
	desktop: { label: "Bureau", width: 1280 },
	fluid: { label: "Fluide", width: null }
};
export type PairViewportKey = keyof typeof PAIR_VIEWPORTS;

const FRAME_HEIGHT = 720;
const runtimeImageComponent = createRuntimeImageComponent();
const runtimeValue = { disableLinks: true, imageComponent: runtimeImageComponent, assetResolver: resolveStorageUrl };

/**
 * Un rendu de page dans une iframe (document écrit en mémoire, feuilles de style du parent
 * clonées, PageRenderer monté par createRoot) : la même technique que l'aperçu de l'éditeur.
 * Le document est mis à l'échelle quand la largeur demandée dépasse la colonne, pour que la
 * page entière reste visible sans défilement horizontal.
 */
const RenderedPageFrame: React.FC<{
	title: string;
	components: All8PageContent;
	viewportWidth: number | null;
	onScroll?: (ratio: number) => void;
	register?: (api: { scrollTo: (ratio: number) => void } | null) => void;
}> = ({ title, components, viewportWidth, onScroll, register }) => {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [containerWidth, setContainerWidth] = useState<number | null>(null);
	const onScrollRef = useRef(onScroll);
	onScrollRef.current = onScroll;
	// Un défilement provoqué par l'autre cadre ne doit pas rebondir vers lui.
	const muteRef = useRef(false);

	useEffect(() => {
		const el = containerRef.current;
		if (!el || typeof ResizeObserver === "undefined") return;
		const update = () => {
			const w = el.getBoundingClientRect().width;
			if (w > 0) setContainerWidth(w);
		};
		update();
		const ro = new ResizeObserver(update);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	useEffect(() => {
		const iframe = iframeRef.current;
		if (!iframe) return;
		const doc = iframe.contentDocument;
		if (!doc) return;

		doc.open();
		doc.write("<!doctype html><html><head></head><body><div id='preview-root'></div></body></html>");
		doc.close();
		document.querySelectorAll("link[rel='stylesheet'], style").forEach((node) => {
			doc.head.appendChild(node.cloneNode(true));
		});
		const mountNode = doc.getElementById("preview-root");
		if (!mountNode) return;

		const root = createRoot(mountNode);
		root.render(
			<RendererRuntimeProvider value={runtimeValue}>
				<div className="page-editor-preview-inner">
					<PageRenderer components={components} />
				</div>
			</RendererRuntimeProvider>
		);

		const handleScroll = () => {
			if (muteRef.current) {
				muteRef.current = false;
				return;
			}
			const scrollingEl = doc.scrollingElement ?? doc.documentElement;
			const max = scrollingEl.scrollHeight - scrollingEl.clientHeight;
			onScrollRef.current?.(max > 0 ? scrollingEl.scrollTop / max : 0);
		};
		const handleClick = (event: Event) => {
			// Aucune navigation depuis un rendu de revue.
			const target = event.target as Element | null;
			if (target?.closest?.("a[href]")) event.preventDefault();
		};
		doc.addEventListener("scroll", handleScroll, true);
		doc.addEventListener("click", handleClick, true);
		register?.({
			scrollTo: (ratio: number) => {
				const scrollingEl = doc.scrollingElement ?? doc.documentElement;
				const max = scrollingEl.scrollHeight - scrollingEl.clientHeight;
				const next = Math.max(0, Math.min(1, ratio)) * Math.max(0, max);
				if (Math.abs(scrollingEl.scrollTop - next) < 1) return;
				muteRef.current = true;
				scrollingEl.scrollTop = next;
			}
		});

		return () => {
			register?.(null);
			doc.removeEventListener("scroll", handleScroll, true);
			doc.removeEventListener("click", handleClick, true);
			// Démontage différé : le nettoyage peut courir pendant un rendu du parent, et React
			// refuse de démonter une racine de façon synchrone à ce moment-là.
			setTimeout(() => root.unmount(), 0);
		};
		// register est stable par construction (useCallback côté parent).
	}, [components, register]);

	const width = viewportWidth ?? containerWidth ?? null;
	const scale = width && containerWidth && width > containerWidth ? containerWidth / width : 1;
	return (
		<div ref={containerRef} className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white" style={{ height: `${Math.round(FRAME_HEIGHT * scale)}px` }}>
			<iframe
				ref={iframeRef}
				title={title}
				data-testid="render-frame"
				style={{
					width: width ? `${width}px` : "100%",
					height: `${FRAME_HEIGHT}px`,
					border: 0,
					background: "white",
					transform: scale < 1 ? `scale(${scale})` : undefined,
					transformOrigin: "top left",
					display: "block"
				}}
			/>
		</div>
	);
};

const FramePlaceholder: React.FC = () => (
	<div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white" style={{ height: `${FRAME_HEIGHT}px` }} data-testid="render-frame-loading">
		<LoadingSpinner size="sm" />
	</div>
);

const EmptySide: React.FC<{ text: string }> = ({ text }) => (
	<div className="flex items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 text-center text-sm text-slate-500" style={{ height: `${FRAME_HEIGHT}px` }}>
		{text}
	</div>
);

/**
 * Un composant dynamique (liste alimentée par un fragment cuit ou résolue par le site au rendu)
 * dont les éléments ne sont pas connus reçoit une liste vide : le renderer du SDK attend un
 * tableau et le site le remplit à la lecture, jamais le payload déployé.
 */
export const normalizeDynamicContent = <T extends { componentType?: string; content?: unknown }>(component: T): T => {
	const content = component?.content as Record<string, unknown> | undefined;
	if (!content || typeof content !== "object") return component;
	const dynamic = typeof content.fragmentKey === "string" || content.mode === "dynamic" || content.dynamicComponent != null;
	if (!dynamic || Array.isArray(content.items)) return component;
	return { ...component, content: { ...content, items: [] } };
};

/** Superpose les fragments cuits de la cible aux composants (widgets calculés et cartes). */
const withFragments = (components: All8PageContent, blocks: BlockFragmentMap, cards: CardFragmentMap): All8PageContent =>
	components.map((component) => normalizeDynamicContent(overlayCardFragments(overlayBlockFragments(component as any, blocks), cards))) as All8PageContent;

/**
 * Le rendu comparé d'un contenu : barre (largeur, défilement lié, lien vers la page) puis les
 * deux cadres. `pair` vient de useDeploymentRenderPair ; ce composant ne charge rien lui-même
 * sauf les fragments cuits de la cible (une fois par montage).
 */
export const DeploymentVisualPair: React.FC<{
	state: RenderPairState;
	target: "preview" | "production";
	language?: string;
}> = ({ state, target, language = "fr" }) => {
	const [viewport, setViewport] = useState<PairViewportKey>("mobile");
	const [linked, setLinked] = useState(true);
	const [fragments, setFragments] = useState<{ blocks: BlockFragmentMap; cards: CardFragmentMap } | null>(null);
	const framesRef = useRef<Record<"left" | "right", { scrollTo: (ratio: number) => void } | null>>({ left: null, right: null });
	const linkedRef = useRef(linked);
	linkedRef.current = linked;

	const pair = state.pair;
	const deployedComponents = useMemo(() => componentsOf(pair?.deployed), [pair?.deployed]);
	const expectedComponents = useMemo(() => componentsOf(pair?.expected), [pair?.expected]);

	// Fragments cuits de la cible : blocs (cours, inflation…) + cartes référencées par les deux côtés.
	useEffect(() => {
		if (!pair?.supported) return;
		let cancelled = false;
		const cardKeys = Array.from(new Set([...collectCardFragmentKeys(deployedComponents), ...collectCardFragmentKeys(expectedComponents)]));
		Promise.all([loadBlockFragments(target, language), loadCardFragments(cardKeys, target, language)])
			.then(([blocks, cards]) => {
				if (!cancelled) setFragments({ blocks, cards });
			})
			.catch(() => {
				if (!cancelled) setFragments({ blocks: {}, cards: {} });
			});
		return () => {
			cancelled = true;
		};
	}, [pair?.supported, deployedComponents, expectedComponents, target, language]);

	// Rendus une seule fois, quand les fragments de la cible sont connus (les widgets et listes
	// s'affichent alors comme sur le site) ; avant, un cadre vide plutôt qu'un double rendu.
	const left = useMemo(
		() => (fragments ? withFragments(deployedComponents, fragments.blocks, fragments.cards) : null),
		[deployedComponents, fragments]
	);
	const right = useMemo(
		() => (fragments ? withFragments(expectedComponents, fragments.blocks, fragments.cards) : null),
		[expectedComponents, fragments]
	);

	const registerLeft = useCallback((api: { scrollTo: (ratio: number) => void } | null) => {
		framesRef.current.left = api;
	}, []);
	const registerRight = useCallback((api: { scrollTo: (ratio: number) => void } | null) => {
		framesRef.current.right = api;
	}, []);
	const onLeftScroll = useCallback((ratio: number) => {
		if (linkedRef.current) framesRef.current.right?.scrollTo(ratio);
	}, []);
	const onRightScroll = useCallback((ratio: number) => {
		if (linkedRef.current) framesRef.current.left?.scrollTo(ratio);
	}, []);

	if (state.loading) {
		return (
			<div className="flex items-center justify-center gap-3 px-5 py-16 text-sm text-slate-500">
				<LoadingSpinner size="sm" /> Préparation du rendu comparé…
			</div>
		);
	}
	if (state.error) return <div className="px-5 py-16 text-center text-sm text-rose-600">{state.error}</div>;
	if (!pair) return null;
	if (!pair.supported) {
		return <div className="px-5 py-16 text-center text-sm text-slate-500">{pair.reason ?? "Ce contenu n'a pas de page publique à comparer."}</div>;
	}

	const targetLabel = target === "production" ? "production" : "preview";
	const siteBase = target === "production" ? PRODUCTION_SITE_URL : PREVIEW_SITE_URL;
	const liveHref = pair.url != null && pair.deployed ? buildPublicUrl(siteBase, pair.url) : null;
	const viewportWidth = PAIR_VIEWPORTS[viewport].width;

	return (
		<div className="space-y-3" data-testid="visual-pair">
			<div className="flex flex-wrap items-center gap-2 px-1">
				<div className="inline-flex rounded-lg border border-slate-200 p-0.5" role="group" aria-label="Largeur du rendu">
					{(Object.keys(PAIR_VIEWPORTS) as PairViewportKey[]).map((key) => (
						<button
							key={key}
							type="button"
							onClick={() => setViewport(key)}
							aria-pressed={viewport === key}
							className={`rounded-md px-3 py-1 text-xs font-semibold transition ${viewport === key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
						>
							{PAIR_VIEWPORTS[key].label}
						</button>
					))}
				</div>
				<button
					type="button"
					onClick={() => setLinked((v) => !v)}
					aria-pressed={linked}
					className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold transition ${linked ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
					title="Faire défiler les deux rendus ensemble"
				>
					<FiLink2 className="h-3 w-3" /> Défilement lié
				</button>
				<span className="ml-auto flex items-center gap-2 text-[11px] text-slate-400">
					{pair.url != null && <span className="font-mono">/{pair.url}</span>}
					{liveHref && (
						<a
							href={liveHref}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 transition hover:bg-slate-50"
						>
							<FiExternalLink className="h-2.5 w-2.5" /> Ouvrir en {targetLabel}
						</a>
					)}
				</span>
			</div>
			{pair.identical && (
				<p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs text-emerald-800">
					Les deux rendus sont identiques : la publication ne changerait rien à cette page.
				</p>
			)}
			{/* Grille inline : les variantes responsives Tailwind sont neutralisées par le CSS du SDK. */}
			<div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: "0.75rem", alignItems: "start" }}>
				<div className="min-w-0 space-y-1.5">
					<p className="text-[11px] font-semibold uppercase tracking-wide text-rose-600">Déployé ({targetLabel})</p>
					{pair.deployed ? (
						left ? (
							<RenderedPageFrame title={`Déployé (${targetLabel})`} components={left} viewportWidth={viewportWidth} onScroll={onLeftScroll} register={registerLeft} />
						) : (
							<FramePlaceholder />
						)
					) : (
						<EmptySide text={`Rien n'est déployé en ${targetLabel} pour ce contenu.`} />
					)}
				</div>
				<div className="min-w-0 space-y-1.5">
					<p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Brouillon admin (après publication)</p>
					{pair.expected ? (
						right ? (
							<RenderedPageFrame title="Brouillon admin" components={right} viewportWidth={viewportWidth} onScroll={onRightScroll} register={registerRight} />
						) : (
							<FramePlaceholder />
						)
					) : (
						<EmptySide text="Le contenu est supprimé côté admin : la publication retirera cette page." />
					)}
				</div>
			</div>
		</div>
	);
};

export default DeploymentVisualPair;
